/**
 * Provides a context for managing state and selectors related to VMS Control components
 * @module VMSContext
 */
import { FORM_MODE, REST_API, STATE_STATUS } from '@awp/constants';
import { GetJson } from '@awp/helpers/fetchReact';
import { BOOTSTRAP_BREAKPOINTS, checkBreakpoint } from '@awp/helpers/UIHelper';
import { debounce } from '@awp/helpers/Utils';
import {
  setStatus,
  hasLoaded,
  fetchSensorEquipmentPacket,
  sensorPlacementItemsDropdownBySensorId,
} from '@awp/stores/sensorEquipmentPacketSlice';
import { selectSensorIdByName } from '@awp/stores/sensorModelSlice';
import { selectAllWeatherStations, selectWeatherStationsBySensorType } from '@awp/stores/weatherStationSlice';
import React, { createContext, useContext, useRef, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * Context for managing state and selectors related to VMS control components
 * @type {React.Context}
 */
const VMSContext = createContext();

/**
 * Provider component for VMSContext.
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - Child components wrapped by the provider.
 * @returns {React.ReactNode} Provider component.
 */
export const VMSContextProvider = ({ children }) => {
  const dispatch = useDispatch();

  const [isMedium, setIsMedium] = useState(checkBreakpoint(BOOTSTRAP_BREAKPOINTS.MEDIUM));
  const [isLarge, setIsLarge] = useState(checkBreakpoint(BOOTSTRAP_BREAKPOINTS.LARGE));
  const [mode, setMode] = useState(FORM_MODE.LIST);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vms, setVms] = useState(null);
  const [search, setSearch] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [selectedWeatherStationId, setSelectedWeatherStationId] = useState(null);
  const [placementLoading, setPlacementLoading] = useState(false);

  const vmsSensorId = useSelector(selectSensorIdByName('VMS'));
  const weatherStations = useSelector(selectWeatherStationsBySensorType(vmsSensorId));
  const sensorPlacements = useSelector(sensorPlacementItemsDropdownBySensorId(vmsSensorId));
  const hasSensorPacketLoaded = useSelector(hasLoaded);

  const weatherStationOptions = useMemo(() => {
    if (!weatherStations) return;

    return weatherStations.map((opt) => {
      if (opt.StatusTypeName === 'Active') return { value: opt.Id, label: opt.DropdownText };
    });
  }, [weatherStations]);

  useEffect(() => {
    setPlacementLoading(true);
    dispatch(setStatus(STATE_STATUS.IDLE));
    dispatch(fetchSensorEquipmentPacket(selectedWeatherStationId));
  }, [selectedWeatherStationId]);

  useEffect(() => {
    if (hasSensorPacketLoaded) {
      setPlacementLoading(false);
    }
  }, [hasSensorPacketLoaded]);

  useMemo(() => {
    setLoading(true);
    const url = REST_API.VMS_VIEW;
    GetJson(url)
      .then((response) => response.json())
      .then((data) => {
        setData(data);
        setFilteredData(data);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const handleResize = debounce(() => {
      setIsMedium(checkBreakpoint(BOOTSTRAP_BREAKPOINTS.MEDIUM));
      setIsLarge(checkBreakpoint(BOOTSTRAP_BREAKPOINTS.LARGE));
    }, 100);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Structure the context values for the provider
  const stateValues = {
    mode,
    isMedium,
    isLarge,
    data,
    loading,
    vms,
    search,
    filteredData,
    selectedWeatherStationId,
    placementLoading,
  };

  const stateSetters = {
    setMode,
    setIsMedium,
    setIsLarge,
    setData,
    setLoading,
    setVms,
    setSearch,
    setFilteredData,
    setSelectedWeatherStationId,
    setPlacementLoading,
  };

  const selectorValues = { sensorPlacements };

  const memoizedValues = { weatherStationOptions };

  // Combine values into a single context value
  const contextValue = {
    ...stateValues,
    ...stateSetters,
    ...selectorValues,
    ...memoizedValues,
  };

  return <VMSContext.Provider value={contextValue}>{children}</VMSContext.Provider>;
};

/**
 * Hook for consuming VMSContext.
 * @returns {object} Context value.
 */
export const useVMSContext = () => useContext(VMSContext);
